import type { FC } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX } from '@/layouts/dashboard/vertical-layout/side-nav-item'
import {
  SIDE_NAV_ITEM_FONT_SIZE,
  sideNavButtonHoverSx,
} from '@/layouts/dashboard/vertical-layout/side-nav-layout-dimensions'
import { densityPx, densityPxStr } from '@/theme/base/density'
import useTranslate from '@/hooks/use-translate'
import { refreshNotificationsPulse } from '../hooks/refreshNotificationsPulse'
import { useNotificationsPulse } from '../hooks/useNotificationsPulse'
import menuBellIcon from '../assets/menu-bell.png'
import { NotificationPanel } from './NotificationPanel'

const unreadCountLabel = (unreadCount: number): string | null => {
  if (unreadCount <= 0) return null
  return unreadCount > 99 ? '+99' : String(unreadCount)
}

const unreadCircleSizePx = (unreadCount: number): number => {
  if (unreadCount > 99) return densityPx(28)
  if (unreadCount > 9) return densityPx(24)
  return densityPx(20)
}

const FOLDED_DOT_SIZE_PX = 8

type NotificationBellProps = {
  isFolded: boolean
  showText: boolean
  onOpenChangelog: () => void
}

export const NotificationBell: FC<NotificationBellProps> = ({
  isFolded,
  showText,
  onOpenChangelog,
}) => {
  const { t } = useTranslate('notifications')
  const queryClient = useQueryClient()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const { unreadCount, headId } = useNotificationsPulse()
  const unreadLabel = unreadCountLabel(unreadCount)
  const circleSize = unreadCircleSizePx(unreadCount)
  const unreadCircleSx = {
    alignItems: 'center',
    backgroundColor: 'primary.main',
    borderRadius: '50%',
    boxSizing: 'border-box',
    color: '#fff',
    display: 'inline-flex',
    flex: '0 0 auto',
    fontSize: unreadCount > 99 ? '10px' : '11px',
    fontWeight: 700,
    height: circleSize,
    justifyContent: 'center',
    lineHeight: 1,
    minWidth: circleSize,
    width: circleSize,
  } as const

  const handleOpen = useCallback(() => {
    setOpen(true)
    void refreshNotificationsPulse(queryClient)
  }, [queryClient])

  return (
    <>
      <Tooltip
        disableHoverListener={!isFolded}
        enterDelay={300}
        placement="right"
        title={t('menuTitle')}
      >
        <Box sx={{ display: 'block', width: '100%' }}>
          <ButtonBase
            ref={buttonRef}
            onClick={handleOpen}
            aria-label={isFolded ? t('menuTitle') : undefined}
            sx={{
              alignItems: 'center',
              borderLeft: `${densityPx(3)}px solid transparent`,
              borderRadius: 1,
              display: 'flex',
              gap: !isFolded ? 1 : 0,
              justifyContent: 'flex-start',
              pl: `${SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX - densityPx(3)}px`,
              pr: densityPxStr(16),
              height: densityPxStr(40),
              textAlign: 'left',
              width: '100%',
              '&:hover': sideNavButtonHoverSx,
            }}
          >
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                flex: '0 0 auto',
                height: densityPxStr(28),
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                aria-hidden
                sx={{
                  backgroundColor: 'currentColor',
                  height: densityPxStr(20),
                  maskImage: `url(${menuBellIcon})`,
                  maskPosition: 'center',
                  maskRepeat: 'no-repeat',
                  maskSize: 'contain',
                  width: densityPxStr(20),
                  WebkitMaskImage: `url(${menuBellIcon})`,
                  WebkitMaskPosition: 'center',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                }}
              />
              {isFolded && unreadLabel && (
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    backgroundColor: 'primary.main',
                    border: '1.5px solid var(--nav-bg)',
                    borderRadius: '50%',
                    height: FOLDED_DOT_SIZE_PX,
                    position: 'absolute',
                    right: 2,
                    top: 2,
                    width: FOLDED_DOT_SIZE_PX,
                  }}
                />
              )}
            </Box>
            {!isFolded && (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexGrow: 1,
                  gap: 1.5,
                  minWidth: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  marginLeft: densityPxStr(3),
                  opacity: showText ? 1 : 0,
                  transform: showText
                    ? 'translateX(0)'
                    : `translateX(-${densityPx(10)}px)`,
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                  pointerEvents: showText ? 'auto' : 'none',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--nav-item-color)',
                    flex: '0 1 auto',
                    fontFamily: (theme) => theme.typography.fontFamily,
                    fontSize: SIDE_NAV_ITEM_FONT_SIZE,
                    fontWeight: 300,
                    lineHeight: densityPxStr(28),
                    minWidth: 0,
                  }}
                >
                  {t('menuTitle')}
                </Typography>
                {unreadLabel && (
                  <Box component="span" sx={unreadCircleSx}>
                    {unreadLabel}
                  </Box>
                )}
              </Box>
            )}
          </ButtonBase>
        </Box>
      </Tooltip>
      <NotificationPanel
        anchorEl={open ? buttonRef.current : null}
        open={open}
        onClose={() => setOpen(false)}
        headId={headId}
        unreadCount={unreadCount}
        onOpenChangelog={onOpenChangelog}
      />
    </>
  )
}
